import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule,
} from "vuex-module-decorators";
import store from "@/store";
import Service from "@/services/RetensiAlihMediaService";
import {
  RetensiSession,
  RetensiChecklistItem,
  RetensiSignature,
  RetensiBeritaAcara,
  ScanKandidatRow,
  ScanKandidatParams,
  SignPayload,
  BeritaAcaraJenis,
  RetensiBatch,
} from "@/types/RetensiAlihMedia";

@Module({
  store,
  dynamic: true,
  namespaced: true,
  name: "retensi-alih-media",
})
class RetensiAlihMediaModule extends VuexModule {
  // ===== State =====
  sessions: RetensiSession[] = [];
  currentSession: RetensiSession | null = null;
  kandidat: ScanKandidatRow[] = [];
  kandidatMeta = {
    cutoff_date: "",
    total_approx: 0,
    per_page: 100,
    current_page: 1,
    limit: 1000,
  };
  loading = false;
  pagination = {
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0,
  };

  // ===== Getters =====
  get isLocked(): boolean {
    const s = this.currentSession?.status;
    return s === "finalized" || s === "locked";
  }

  get checklistByKelompok(): Record<string, RetensiChecklistItem[]> {
    const items = this.currentSession?.checklist_items ?? [];
    const grouped: Record<string, RetensiChecklistItem[]> = {};
    for (const it of items) {
      if (!grouped[it.kelompok]) grouped[it.kelompok] = [];
      grouped[it.kelompok].push(it);
    }
    return grouped;
  }

  get temuanKritis(): RetensiChecklistItem[] {
    return (this.currentSession?.checklist_items ?? []).filter(
      (i) => i.is_temuan_kritis
    );
  }

  get signaturesByRole(): Record<string, RetensiSignature> {
    const sigs = this.currentSession?.signatures ?? [];
    const out: Record<string, RetensiSignature> = {};
    for (const s of sigs) out[s.role] = s;
    return out;
  }

  // ===== Mutations =====
  @Mutation SET_SESSIONS(val: RetensiSession[]) {
    this.sessions = val;
  }

  @Mutation SET_CURRENT(val: RetensiSession | null) {
    this.currentSession = val;
  }

  @Mutation PATCH_CURRENT(patch: Partial<RetensiSession>) {
    if (this.currentSession) {
      this.currentSession = { ...this.currentSession, ...patch };
    }
  }

  @Mutation SET_KANDIDAT(payload: {
    data: ScanKandidatRow[];
    meta: typeof RetensiAlihMediaModule.prototype.kandidatMeta;
  }) {
    this.kandidat = payload.data;
    this.kandidatMeta = payload.meta;
  }

  @Mutation SET_LOADING(val: boolean) {
    this.loading = val;
  }

  @Mutation SET_PAGINATION(p: typeof RetensiAlihMediaModule.prototype.pagination) {
    this.pagination = p;
  }

  // ===== Actions =====

  @Action async fetchSessions(params: any = {}) {
    this.SET_LOADING(true);
    try {
      const res: any = await Service.listSessions(params);
      // res is the raw axios response; res.data is the Laravel paginator
      // ({ data: [], current_page, ... }) — unwrap to the row array for the table.
      const body = res && res.data !== undefined ? res.data : res;
      const rows = Array.isArray(body) ? body : (body && body.data) || [];
      this.SET_SESSIONS(rows);
      const meta = Array.isArray(body) ? null : body;
      if (meta && meta.current_page) {
        this.SET_PAGINATION({
          currentPage: meta.current_page,
          lastPage: meta.last_page,
          perPage: meta.per_page,
          total: meta.total,
        });
      }
    } finally {
      this.SET_LOADING(false);
    }
  }

  @Action async fetchSession(id: number) {
    this.SET_LOADING(true);
    try {
      const res: any = await Service.showSession(id);
      this.SET_CURRENT(res.data ?? res);
    } finally {
      this.SET_LOADING(false);
    }
  }

  @Action async createSession(payload: Partial<RetensiSession>) {
    const res: any = await Service.createSession(payload);
    this.SET_CURRENT(res.data ?? res);
    return res;
  }

  @Action async updateSession(payload: {
    id: number;
    data: Partial<RetensiSession>;
  }) {
    const res: any = await Service.updateSession(payload.id, payload.data);
    this.SET_CURRENT(res.data ?? res);
    return res;
  }

  @Action async scanKandidat(params: ScanKandidatParams) {
    this.SET_LOADING(true);
    try {
      const res: any = await Service.scanKandidat(params);
      this.SET_KANDIDAT({ data: res.data, meta: res.meta });
      return res;
    } finally {
      this.SET_LOADING(false);
    }
  }

  @Action async createBatch(payload: {
    sessionId: number;
    data: Partial<RetensiBatch>;
  }) {
    return await Service.createBatch(payload.sessionId, payload.data);
  }

  @Action async uploadDokumen(payload: { batchId: number; formData: FormData }) {
    return await Service.uploadDokumen(payload.batchId, payload.formData);
  }

  @Action async bulkSaveChecklist(payload: {
    sessionId: number;
    items: Partial<RetensiChecklistItem>[];
  }) {
    const res: any = await Service.bulkSaveChecklist(
      payload.sessionId,
      payload.items
    );
    // refresh current session
    if (this.currentSession?.id === payload.sessionId) {
      await this.fetchSession(payload.sessionId);
    }
    return res;
  }

  @Action async sign(payload: { sessionId: number; data: SignPayload }) {
    const res: any = await Service.sign(payload.sessionId, payload.data);
    if (this.currentSession?.id === payload.sessionId) {
      await this.fetchSession(payload.sessionId);
    }
    return res;
  }

  @Action async createBeritaAcara(payload: {
    sessionId: number;
    data: {
      jenis: BeritaAcaraJenis;
      tgl_ba?: string;
      metode_pemusnahan?: string;
      ringkasan?: Record<string, any>;
    };
  }) {
    const res: any = await Service.createBeritaAcara(
      payload.sessionId,
      payload.data
    );
    if (this.currentSession?.id === payload.sessionId) {
      await this.fetchSession(payload.sessionId);
    }
    return res;
  }

  @Action async finalize(sessionId: number) {
    const res: any = await Service.finalize(sessionId);
    this.SET_CURRENT(res.data ?? res);
    return res;
  }

}

export default getModule(RetensiAlihMediaModule);
